/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import { cssMap as cssMapCompiled } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { cssMap, cx, jsx } from '@atlaskit/css';
import RovoChatIcon from '@atlaskit/icon/core/rovo-chat';
import SearchIcon from '@atlaskit/icon/core/search';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box, Inline, Pressable } from '@atlaskit/primitives/compiled';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { FollowUpButton } from '@atlassian/conversation-assistant-ui-components/ui/follow-up-button';
import { getFrontendExperiment } from '@atlassian/search-experiment/frontend';

import { SainAction } from '../../common/controller/analytics/hooks/sain-action';
import { useSAINUsageCriteriaMet } from '../../common/controller/analytics/hooks/use-sain-usage';
import { OpenChatButton } from '../../common/ui/open-chat/open-chat-button';
import { SAINFeatureControl } from '../../common/utils/sain-feature-control';
import { useAIAnswerContext } from '../ai-answer-context';
import { onErrorStateSuggestedQueriesShown } from '../on-error-state-suggested-queries-shown';
import { onErrorStateSuggestedQueryClick } from '../on-error-state-suggested-query-click';
import { onExpandButtonClicked } from '../on-expand-button-clicked';
import { onFollowUpSuggestionClick } from '../on-follow-up-suggestion-click';

import { AIAnswerFollowUpPill } from './ai-answer-follow-up-pill';
import { AIAnswerFollowUpPillText } from './ai-answer-follow-up-pill-text';
import { messages } from './messages';
import { useOverflowDetection } from './use-overflow-detection';

const stylesCompiled = cssMapCompiled({
	pillLineHeight: {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography -- Ignored via go/DSP-18766
		lineHeight: '16px',
	},
});

const styles = cssMap({
	button: {
		color: token('color.text.subtle'),
		paddingTop: token('space.075'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.150'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.small'),
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		flexShrink: 1,
		backgroundColor: token('color.background.neutral.subtle'),

		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},

	queryText: {
		wordBreak: 'break-word',
		textAlign: 'left',
		'&::first-letter': {
			textTransform: 'capitalize',
		},
	},

	whiteSpacePreWrap: {
		whiteSpace: 'pre-wrap',
	},
	luminaFollowUpButtonStyleOverrides: {
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.small'),
		minWidth: '0px',
		flex: '0 1 auto',
	},
	luminaFollowUpButtonStyleOverridesImproved: {
		backgroundColor: token('elevation.surface'),
	},
	luminaFollowUpButtonNoShrink: {
		flex: '0 0 auto',
	},
	pillHidden: {
		display: 'none',
	},
	pillContainer: {
		display: 'flex',
		gap: token('space.100'),
		alignItems: 'center',
		overflow: 'hidden',
		flexWrap: 'nowrap',
	},
	pillContainerExpanded: {
		overflow: 'visible',
		flexWrap: 'wrap',
	},
	pillContainerInner: {
		display: 'flex',
		gap: token('space.100'),
		alignItems: 'center',
		overflow: 'hidden',
		flexShrink: 1,
		minWidth: '0px',
	},
	upliftWrapper: {
		width: '100%',
	},
});

// Stable reference: this feeds a useMemo dependency array.
const NO_FOLLOW_UPS: string[] = [];

const motionStyles = cssMap({
	button: {
		transition: token('motion.button.hovered'),
		'&:active': {
			transition: token('motion.button.pressed'),
		},
	},
});

export const FollowUpSuggestionFooter: ({
	omitDialogues,
}: {
	omitDialogues?: boolean;
}) => JSX.Element[] | null = ({ omitDialogues = false }: { omitDialogues?: boolean }) => {
	const {
		commonAttributes,
		fireAnalyticsEvent,
		setQuery,
		updateContextCache,
		onClose,
		closeParentDialog,
		callbacks,
		showChatFollowUps,
	} = useAIAnswerContext();
	const {
		followUps: backendFollowUps,
		query,
		answerString,
		searchSessionId,
		errorStateSuggestedQueries,
	} = commonAttributes;
	// Pills only. Do not move this onto commonAttributes.followUps: AIAnswerFooter mounts the
	// section holding Open chat off its length.
	const followUps = fg('kd_sain_disable_llm_follow_ups') ? NO_FOLLOW_UPS : backendFollowUps;
	const publish = usePublish('ai-mate');
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const { formatMessage } = useIntl();
	const isUXUplift = SAINFeatureControl.isSAINUXUpliftEnabled();
	const isImprovedChatOptions = SAINFeatureControl.isImprovedChatOptionsEnabled();
	const [showMore, setShowMore] = useState(false);
	const { containerRef, isOverflowing } = useOverflowDetection();

	// Reset expanded state when follow-ups change (e.g. new query).
	// Use a stable string comparison instead of the array reference to avoid resetting
	// on every render when commonAttributes produces a new array object each time.
	const followUpsKey = followUps.join('\x00');
	useEffect(() => {
		setShowMore(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [followUpsKey]);

	const showNLPFollowUps = followUps.length > 0;

	const { serveExperiment: serveErrorStateSuggestedQueriesExp } = getFrontendExperiment({
		abTestId: 'kd_sain_show_no_answer_suggestions_exp',
	});

	const visibleFollowUps = useMemo(() => {
		return showNLPFollowUps
			? followUps.slice(0, 2)
			: serveErrorStateSuggestedQueriesExp
				? errorStateSuggestedQueries.slice(0, 2)
				: [];
	}, [showNLPFollowUps, followUps, errorStateSuggestedQueries, serveErrorStateSuggestedQueriesExp]);

	const showErrorStateSuggestedQueries = visibleFollowUps.length > 0 && !showNLPFollowUps;

	const onPillClick = useCallback(
		(followUp: string) => {
			if (showChatFollowUps) {
				publish({
					type: 'chat-new',
					source: 'sain',
					data: {
						name: followUp.substring(0, 30),
						dialogues: omitDialogues
							? []
							: [
									{
										human_message: { content: query },
										agent_message: { content: answerString },
									},
								],
						prompt: followUp,
						sourceId: searchSessionId,
					},
				});
			} else {
				setQuery(followUp);
				updateContextCache({ fromFollowUp: true });
			}

			fireAnalyticsEvent(
				onFollowUpSuggestionClick({
					...commonAttributes,
					followUpString: followUp,
					destination: 'chat',
				}),
			);
			fireSAINUsageEvent({
				metCriteria: SainAction.FOLLOWUP_CLICKED,
				commonAttributes,
			});

			onClose?.();
			closeParentDialog?.();
			callbacks?.onFollowUpSuggestionClick?.();
		},
		[
			fireSAINUsageEvent,
			fireAnalyticsEvent,
			commonAttributes,
			onClose,
			closeParentDialog,
			callbacks,
			publish,
			query,
			answerString,
			searchSessionId,
			setQuery,
			updateContextCache,
			showChatFollowUps,
			omitDialogues,
		],
	);

	const onSuggestedQueryClick = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>, suggestedQuery: string) => {
			e.preventDefault();
			e.stopPropagation();

			setQuery(suggestedQuery);
			fireAnalyticsEvent(
				onErrorStateSuggestedQueryClick({
					...commonAttributes,
					errorStateSuggestedQuery: suggestedQuery,
				}),
			);
			fireSAINUsageEvent({
				metCriteria: SainAction.ERROR_STATE_SUGGESTED_QUERY_CLICKED,
				commonAttributes,
			});
			onClose?.();
			closeParentDialog?.();
		},
		[
			setQuery,
			fireAnalyticsEvent,
			fireSAINUsageEvent,
			onClose,
			closeParentDialog,
			commonAttributes,
		],
	);

	const onSeeMoreClick = useCallback(() => {
		fireAnalyticsEvent(
			onExpandButtonClicked({
				...commonAttributes,
				expandButtonSource: 'followupSeeMore',
			}),
		);
		setShowMore(true);
	}, [fireAnalyticsEvent, commonAttributes]);

	useEffect(() => {
		if (showErrorStateSuggestedQueries) {
			fireAnalyticsEvent(
				onErrorStateSuggestedQueriesShown({
					...commonAttributes,
					showErrorStateSuggestedQueries: visibleFollowUps,
				}),
			);
		}
	}, [showErrorStateSuggestedQueries, fireAnalyticsEvent, commonAttributes, visibleFollowUps]);

	if (isUXUplift && showChatFollowUps) {
		const allFollowUps = isImprovedChatOptions ? followUps.slice(0, 2) : followUps.slice(0, 1);
		// Collapse only when gate is on, there are multiple follow-ups, and they overflow
		const isCollapsed = isImprovedChatOptions && isOverflowing && allFollowUps.length > 1;

		const pillBox = (followUp: string, hidden: boolean) => (
			<Box
				data-pill
				xcss={cx(
					hidden && styles.pillHidden,
					styles.luminaFollowUpButtonNoShrink,
					styles.luminaFollowUpButtonStyleOverrides,
					isImprovedChatOptions && styles.luminaFollowUpButtonStyleOverridesImproved,
				)}
				key={followUp}
			>
				<Tooltip content={followUp} position="top">
					<Button
						iconBefore={RovoChatIcon}
						onClick={() => onPillClick(followUp)}
						testId="smart-answer-follow-up-button"
					>
						{followUp}
					</Button>
				</Tooltip>
			</Box>
		);

		if (isImprovedChatOptions) {
			return [
				<Box xcss={styles.upliftWrapper} key="uplift-wrapper">
					<Box xcss={styles.pillContainer}>
						{/* Measured container — only contains actual pills, never "See more",
						    so ResizeObserver on this div is never triggered by "See more" appearing */}
						<Box
							ref={containerRef}
							xcss={cx(styles.pillContainerInner, showMore && styles.pillContainerExpanded)}
						>
							{/* Open Chat always visible */}
							<Box
								data-pill
								xcss={cx(
									styles.luminaFollowUpButtonStyleOverrides,
									styles.luminaFollowUpButtonNoShrink,
									styles.luminaFollowUpButtonStyleOverridesImproved,
								)}
							>
								<OpenChatButton
									prompt={query}
									query={query}
									answerString={answerString}
									source="sain"
								/>
							</Box>
							{/* Always render all follow-ups for measurement; hide extras when collapsed */}
							{allFollowUps.map((followUp, i) =>
								pillBox(followUp, isCollapsed && i > 0 && !showMore),
							)}
						</Box>
						{/* See more button — outside the measured container so it never
						    triggers ResizeObserver and causes a flash */}
						{isCollapsed && !showMore && (
							<Box
								xcss={cx(
									styles.luminaFollowUpButtonStyleOverrides,
									styles.luminaFollowUpButtonStyleOverridesImproved,
								)}
							>
								<Button
									appearance="default"
									onClick={onSeeMoreClick}
									testId="smart-answer-follow-up-expand-button"
								>
									{formatMessage(messages.ai_answer_followup_see_more)}
								</Button>
							</Box>
						)}
					</Box>
				</Box>,
			];
		}

		return [
			<Box
				xcss={cx(styles.luminaFollowUpButtonStyleOverrides, styles.luminaFollowUpButtonNoShrink)}
				key="open-chat"
			>
				<OpenChatButton prompt={query} query={query} answerString={answerString} source="sain" />
			</Box>,
			...allFollowUps.map((followUp) => pillBox(followUp, false)),
		];
	}

	return visibleFollowUps.length > 0
		? visibleFollowUps.map((followUp, index) => {
				if (showChatFollowUps && !showErrorStateSuggestedQueries) {
					return (
						<Inline space="space.050" alignBlock="center" key={followUp}>
							<FollowUpButton
								onClick={() => onPillClick(followUp)}
								followUpText={followUp}
								followUpIndex={index}
								followUpType="UNAVAILABLE"
								experienceId="sain"
							/>
						</Inline>
					);
				}

				if (showErrorStateSuggestedQueries) {
					return (
						<Inline space="space.050" alignBlock="center" key={followUp}>
							<Pressable
								xcss={cx(
									styles.button,
									fg('platform-dst-motion-uplift-custom-button') && motionStyles.button,
								)}
								onClick={(e) => onSuggestedQueryClick(e, followUp)}
							>
								<div css={stylesCompiled.pillLineHeight}>
									<Inline space="space.075" alignBlock="baseline">
										<SearchIcon color="currentColor" label="" />
										<Box xcss={cx(styles.queryText, styles.whiteSpacePreWrap)}>{followUp}</Box>
									</Inline>
								</div>
							</Pressable>
						</Inline>
					);
				}

				return (
					<Inline space="space.050" alignBlock="center" key={followUp}>
						<AIAnswerFollowUpPill onClick={() => onPillClick(followUp)}>
							<SearchIcon label="" color={token('color.icon')} />
							<AIAnswerFollowUpPillText>{followUp}</AIAnswerFollowUpPillText>
						</AIAnswerFollowUpPill>
					</Inline>
				);
			})
		: null;
};
